import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  parseHeaderLine,
  tokenizeLine,
  displayOffsetToRawOffset,
  rawOffsetToDisplayOffset,
  applyPunchlineToggle,
  applyStageToggle,
  parseBits,
  getBitTimings,
  formatTime,
  DEFAULT_WORDS_PER_MINUTE,
} from "./documentBodyUtils";

/**
 * WYSIWYG editor for "Write" mode.
 *
 * The raw text format (==, (), *, []) never changes - `body` is still a
 * plain string. A bit header always shows just its name (never "=="),
 * and a punchline always shows just its bold word/phrase (never the
 * "*"/"()" marker) - you edit the bold text directly, the same way you'd
 * edit bold text in a normal rich text editor.
 *
 * The whole multi-line body is ONE contentEditable region, not one per
 * line, specifically so native selection/copy/paste work across line
 * boundaries - browsers won't let a drag-selection cross from one
 * contentEditable element into a separate one, so if every line were its
 * own isolated contentEditable (an earlier version of this file), you
 * could only ever select within a single line. Each line is still its
 * own <div> child (for block layout, and so we can tell lines apart via
 * data-header), but editability belongs to the container as a whole.
 *
 * Copy/cut put TWO things on the clipboard: the clean display text (no
 * "*", "()", "==", "[]") as text/plain, for pasting into some other
 * app - copying out of a rich-text editor should hand you the readable
 * words, the same way copying bold text out of a word processor doesn't
 * hand you markdown asterisks - and the exact raw text tucked into a
 * text/html payload (buildClipboardHtml/extractRawTextFromClipboardHtml)
 * so a punchline or header pasted back into this editor (or into raw
 * view) keeps its formatting instead of losing it to the plain-text
 * round trip. Paste prefers that raw payload when present, and falls
 * back to text/plain (treated as raw syntax, same as typing - pasting
 * "*word" from outside always makes it a punchline too) otherwise. This
 * also makes a multi-line paste (or a paste that replaces a multi-line
 * selection) land as the right raw lines rather than however the browser
 * would natively split/merge the blocks.
 *
 * On every edit, the whole raw body is reconstructed by walking the live
 * DOM (reconstructRawLine per line) and fed back through `setBody`; the
 * container's HTML is then rebuilt from that raw text, touching the DOM
 * only when the computed HTML actually differs from what's already
 * there (the dangerouslySetInnerHTML below). That's what lets most
 * editing - typing, and native backspace/delete merging two lines at a
 * boundary - be left entirely to the browser: whatever shape the DOM
 * ends up in, reconstruction reads it back out correctly, and any
 * mismatch between React's and the browser's idea of the DOM gets wiped
 * by the next full replace rather than fought over node-by-node (which
 * is what caused duplicated characters in an earlier version of this).
 * Enter is still fully hand-rolled (for header-aware line splitting),
 * as is Backspace at the very start of a header's name (to demote it to
 * a plain line instead of immediately merging upward).
 *
 * Perform/Practice mode rendering (DocumentBody.js / PracticeBody.js) is
 * untouched by this file.
 *
 * The toolbar buttons themselves (Punchline, Bit, Stage Direction) live
 * above this component - rendered by DocumentBody, under the editor
 * menu - rather than inside it; this component exposes `applyBold()`,
 * `applyBitHeader()` and `applyStageDirection()` via `ref` for that
 * toolbar (and the matching Edit-menu items) to call, and reports
 * selection changes via `onActiveLineChange` so they can disable
 * themselves when the selection isn't inside a single line.
 */

// Returns the character offset of `point` (or the current selection's
// start, if no point is given) within `lineEl`'s displayed text.
function getCaretOffset(lineEl, point) {
  if (!point) {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return 0;
    const r = sel.getRangeAt(0);
    point = { startContainer: r.startContainer, startOffset: r.startOffset };
  }
  if (!lineEl || !lineEl.contains(point.startContainer)) return 0;
  const preRange = document.createRange();
  preRange.selectNodeContents(lineEl);
  preRange.setEnd(point.startContainer, point.startOffset);
  return preRange.toString().length;
}

// Moves the caret to a character offset within `lineEl`'s displayed text.
function setCaretOffset(lineEl, offset) {
  const sel = window.getSelection();
  if (!sel) return;
  let remaining = offset;
  let targetNode = null;
  let targetOffset = 0;

  (function walk(el) {
    for (const child of el.childNodes) {
      if (targetNode) return;
      if (child.nodeType === Node.TEXT_NODE) {
        const len = child.textContent.length;
        if (remaining <= len) {
          targetNode = child;
          targetOffset = remaining;
          return;
        }
        remaining -= len;
      } else {
        walk(child);
      }
    }
  })(lineEl);

  const range = document.createRange();
  if (targetNode) {
    range.setStart(targetNode, targetOffset);
  } else {
    range.setStart(lineEl, lineEl.childNodes.length);
  }
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

// Walks a (non-header) line's live DOM and rebuilds its raw text: a <b>
// (a punchline) becomes "*word" or "(phrase)", an <i> (a stage direction)
// is kept as-is since its display text already includes the brackets,
// and everything else is kept as plain text. This is what lets the user
// type directly into the bold/italic text and have the raw syntax come
// along for free.
//
// A <b>'s original marker ("*" vs "()") is read from its data-marker
// attribute so an edit elsewhere on the line never silently rewrites an
// untouched punchline's marker (e.g. turning "(word)" into "*word" just
// because "word" happens to be a single word right now) - the raw format
// should only change where the user actually typed. Whitespace inside
// the bold text always forces the "()" form, since "*" can't represent
// one, regardless of the original marker.
function reconstructRawLine(el) {
  let raw = "";
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      raw += node.nodeValue;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.tagName === "B") {
      const text = node.textContent;
      if (!text) return;
      const marker = node.getAttribute("data-marker");
      raw += marker === "paren" || /\s/.test(text) ? `(${text})` : `*${text}`;
    } else if (node.tagName === "I") {
      raw += node.textContent;
    } else {
      Array.from(node.childNodes).forEach(walk);
    }
  };
  Array.from(el.childNodes).forEach(walk);
  return raw;
}

// Rebuilds the full raw body by walking every top-level node currently in
// the container's DOM, regardless of how that structure got there
// (native typing, or the browser's native backspace/delete merging two
// lines at a boundary) - this is what makes it safe to leave most
// editing to the browser: whatever shape the DOM ends up in, this reads
// it back out correctly. A stray text node with no line <div> wrapper
// (an edge case some browsers produce when a totally empty editor is
// typed into) is treated as its own line too, rather than dropped.
function reconstructBody(containerEl) {
  return Array.from(containerEl.childNodes)
    .map((node) => {
      if (node.nodeType === Node.TEXT_NODE) return node.nodeValue;
      if (node.nodeType !== Node.ELEMENT_NODE) return "";
      if (node.getAttribute("data-header") === "true") return `== ${node.textContent}`;
      return reconstructRawLine(node);
    })
    .join("\n");
}

function escapeHtml(str) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function renderContentLineHtml(line) {
  return tokenizeLine(line)
    .map((t) => {
      if (t.type === "punchline") return `<b data-marker="${t.marker}">${escapeHtml(t.display)}</b>`;
      if (t.type === "stage") return `<i class="text-gray-500 italic">${escapeHtml(t.display)}</i>`;
      return escapeHtml(t.display);
    })
    .join("");
}

const LINE_CLASS = "whitespace-pre-wrap break-words px-2 min-h-[1.4em]";
const HEADER_CLASS = `${LINE_CLASS} wysiwyg-bit-header relative font-semibold text-emerald-900 bg-gray-100 border border-gray-300 rounded text-center py-1 my-1`;

function buildLineHtml(line) {
  const header = parseHeaderLine(line);
  if (header) {
    return `<div data-header="true" class="${HEADER_CLASS}">${escapeHtml(header.name)}</div>`;
  }
  return `<div data-header="false" class="${LINE_CLASS}">${renderContentLineHtml(line)}</div>`;
}

function buildBodyHtml(lines) {
  return lines.map(buildLineHtml).join("");
}

// "start–end" timing label for each bit header, in header order - the
// same numbers Perform mode shows. parseBits only emits a nameless bit
// for content above the first header, so the named bits line up 1:1
// with the header <div>s.
function getHeaderTimingLabels(body, wordsPerMinute) {
  const bits = parseBits(body);
  const timings = getBitTimings(bits, wordsPerMinute);
  return bits
    .map((bit, i) => (bit.name === null ? null : `${formatTime(timings[i].start)}–${formatTime(timings[i].end)}`))
    .filter((label) => label !== null);
}

// Finds which line <div> (a direct child of `containerEl`) contains
// `node`, returning its index, or null if `node` isn't inside any line.
function getLineIndexAt(containerEl, node) {
  if (!containerEl || !node) return null;
  let lineEl = node.nodeType === Node.ELEMENT_NODE ? node : node.parentNode;
  while (lineEl && lineEl.parentNode !== containerEl) {
    lineEl = lineEl.parentNode;
  }
  if (!lineEl) return null;
  const index = Array.from(containerEl.children).indexOf(lineEl);
  return index === -1 ? null : index;
}

// Reads the current window selection (if it's inside `containerEl`) as a
// normalized { start, end, collapsed } range, each endpoint given as a
// raw-text { lineIndex, rawOffset } - which may span multiple lines.
// Returns null if there's no usable selection.
function getSelectionRange(containerEl, lines) {
  if (!containerEl) return null;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!containerEl.contains(range.startContainer) || !containerEl.contains(range.endContainer)) return null;

  const startLine = getLineIndexAt(containerEl, range.startContainer);
  const endLine = getLineIndexAt(containerEl, range.endContainer);
  if (startLine == null || endLine == null) return null;

  const startEl = containerEl.children[startLine];
  const endEl = containerEl.children[endLine];
  const startDisplay = getCaretOffset(startEl, { startContainer: range.startContainer, startOffset: range.startOffset });
  const endDisplay = getCaretOffset(endEl, { startContainer: range.endContainer, startOffset: range.endOffset });
  const startRaw = displayOffsetToRawOffset(lines[startLine] ?? "", startDisplay);
  const endRaw = displayOffsetToRawOffset(lines[endLine] ?? "", endDisplay);

  const a = { lineIndex: startLine, rawOffset: startRaw };
  const b = { lineIndex: endLine, rawOffset: endRaw };
  const [start, end] =
    a.lineIndex < b.lineIndex || (a.lineIndex === b.lineIndex && a.rawOffset <= b.rawOffset) ? [a, b] : [b, a];

  return { start, end, collapsed: sel.isCollapsed };
}

// A line's full display text (markers stripped) - what tokenizeLine's
// "display" fields concatenate to, or a header's bare name.
function getDisplayText(line) {
  const header = parseHeaderLine(line);
  if (header) return header.name;
  return tokenizeLine(line)
    .map((t) => t.display)
    .join("");
}

// Clean, markup-free text for a (possibly multi-line) selection, joined
// with "\n" - what Copy/Cut put on the clipboard. Deliberately the
// *display* text (no "*", "()", "==", "[]"), not the raw storage format:
// copying out of a rich-text editor should give you the readable words,
// the same way copying bold text out of a word processor doesn't hand
// you markdown asterisks.
function extractDisplayText(lines, start, end) {
  const startDisplay = rawOffsetToDisplayOffset(lines[start.lineIndex] ?? "", start.rawOffset);
  const endDisplay = rawOffsetToDisplayOffset(lines[end.lineIndex] ?? "", end.rawOffset);
  if (start.lineIndex === end.lineIndex) {
    return getDisplayText(lines[start.lineIndex] ?? "").slice(startDisplay, endDisplay);
  }
  const parts = [getDisplayText(lines[start.lineIndex] ?? "").slice(startDisplay)];
  for (let i = start.lineIndex + 1; i < end.lineIndex; i++) parts.push(getDisplayText(lines[i] ?? ""));
  parts.push(getDisplayText(lines[end.lineIndex] ?? "").slice(0, endDisplay));
  return parts.join("\n");
}

// Raw text (markers included) for a (possibly multi-line) selection,
// joined with "\n" - carried alongside the clean display text on the
// clipboard (see buildClipboardHtml) so a punchline/header pasted back
// into this editor (or into raw view) keeps its formatting, even though
// a paste into some other app only ever sees the clean plain text.
function extractRawText(lines, start, end) {
  if (start.lineIndex === end.lineIndex) {
    return (lines[start.lineIndex] ?? "").slice(start.rawOffset, end.rawOffset);
  }
  const parts = [(lines[start.lineIndex] ?? "").slice(start.rawOffset)];
  for (let i = start.lineIndex + 1; i < end.lineIndex; i++) parts.push(lines[i] ?? "");
  parts.push((lines[end.lineIndex] ?? "").slice(0, end.rawOffset));
  return parts.join("\n");
}

// Builds the text/html clipboard payload: the clean display text to show
// if pasted somewhere that renders HTML, plus the exact raw text tucked
// into a data attribute so a paste back into this editor can recover it
// losslessly instead of re-deriving it from the (lossy, markup-free)
// plain-text fallback.
function buildClipboardHtml(rawText, displayText) {
  return `<span data-jokelab-raw="${encodeURIComponent(rawText)}">${escapeHtml(displayText)}</span>`;
}

// Recovers the raw text embedded by buildClipboardHtml, or null if this
// HTML wasn't produced by this editor (pasted from elsewhere).
function extractRawTextFromClipboardHtml(html) {
  const match = html && html.match(/data-jokelab-raw="([^"]*)"/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

// Replaces the raw text from `start` to `end` (spanning however many
// lines that covers) with `insertedText` (itself possibly multi-line),
// returning the resulting lines array and where the caret should land.
// Used for paste (insertedText = clipboard contents) and cut/Enter
// (insertedText = "", to just delete the selection).
function replaceRange(lines, start, end, insertedText) {
  const insertedLines = insertedText.split("\n");
  const before = (lines[start.lineIndex] ?? "").slice(0, start.rawOffset);
  const after = (lines[end.lineIndex] ?? "").slice(end.rawOffset);

  const newLines = lines.slice(0, start.lineIndex);
  let caretLine;
  let caretRaw;
  if (insertedLines.length === 1) {
    newLines.push(before + insertedLines[0] + after);
    caretLine = start.lineIndex;
    caretRaw = (before + insertedLines[0]).length;
  } else {
    newLines.push(before + insertedLines[0]);
    for (let i = 1; i < insertedLines.length - 1; i++) newLines.push(insertedLines[i]);
    newLines.push(insertedLines[insertedLines.length - 1] + after);
    caretLine = start.lineIndex + insertedLines.length - 1;
    caretRaw = insertedLines[insertedLines.length - 1].length;
  }
  newLines.push(...lines.slice(end.lineIndex + 1));
  return { lines: newLines, caretLine, caretRaw };
}

const WysiwygBody = forwardRef(function WysiwygBody(
  { body, setBody, disabled, onEditAttempt, onActiveLineChange, wordsPerMinute = DEFAULT_WORDS_PER_MINUTE },
  ref
) {
  const lines = body.split("\n");
  const containerRef = useRef(null);
  const pendingCaret = useRef(null);
  const [, setActiveLineState] = useState(null);

  const canEdit = () => !disabled && (!onEditAttempt || onEditAttempt());

  // A hand-rolled undo/redo stack. The browser's own native undo doesn't
  // work here: it tracks DOM mutations made via real typing/editing
  // commands, but this component rewrites the DOM via `innerHTML =` (see
  // the dangerouslySetInnerHTML note below) whenever the computed HTML
  // differs from what's there, which isn't a tracked "undoable" edit and
  // resets whatever the browser thought its undo history was - so Ctrl/
  // Cmd+Z is handled entirely ourselves instead of relying on it.
  //
  // Each entry is { body, caret } so undo/redo also restores the cursor.
  // Plain typing coalesces into the current entry (so undo removes a
  // whole burst of typing, not one character at a time) as long as it
  // keeps happening within COALESCE_MS of the last keystroke; any other
  // edit (Enter, paste, cut, Punchline/Bit/Stage) always starts a new
  // entry. If `body` ever changes for a reason other than our own
  // setBody calls below - an AI rewrite, switching to/from raw view,
  // loading a different document - the history resets to that new value,
  // so undo can never land on a stale structure that doesn't match it.
  const COALESCE_MS = 600;
  const historyRef = useRef({ stack: [{ body, caret: null }], index: 0, lastPushAt: 0, coalescing: false });

  useEffect(() => {
    const h = historyRef.current;
    if (body !== h.stack[h.index].body) {
      historyRef.current = { stack: [{ body, caret: null }], index: 0, lastPushAt: 0, coalescing: false };
    }
  }, [body]);

  const recordHistory = (newBody, caret, coalesce) => {
    const h = historyRef.current;
    const now = Date.now();
    if (coalesce && h.coalescing && now - h.lastPushAt < COALESCE_MS && h.index === h.stack.length - 1) {
      h.stack[h.index] = { body: newBody, caret };
    } else {
      h.stack = h.stack.slice(0, h.index + 1);
      h.stack.push({ body: newBody, caret });
      h.index = h.stack.length - 1;
    }
    h.lastPushAt = now;
    h.coalescing = coalesce;
  };

  const undo = () => {
    const h = historyRef.current;
    if (h.index === 0) return;
    h.index -= 1;
    h.coalescing = false;
    const entry = h.stack[h.index];
    if (entry.caret) pendingCaret.current = entry.caret;
    setBody(entry.body);
  };

  const redo = () => {
    const h = historyRef.current;
    if (h.index >= h.stack.length - 1) return;
    h.index += 1;
    h.coalescing = false;
    const entry = h.stack[h.index];
    if (entry.caret) pendingCaret.current = entry.caret;
    setBody(entry.body);
  };

  const commit = (newLines, caret) => {
    if (!canEdit()) return;
    if (caret) pendingCaret.current = caret;
    const newBody = newLines.join("\n");
    recordHistory(newBody, caret ?? null, false);
    setBody(newBody);
  };

  // Restore the caret after any render that set `pendingCaret` - either
  // because we just reconstructed the raw body (caret should stay right
  // where it was) or a structural change (Enter/paste/bold/undo/etc)
  // moved it on purpose.
  useEffect(() => {
    if (!pendingCaret.current) return;
    const { lineIndex, offset } = pendingCaret.current;
    pendingCaret.current = null;
    const containerEl = containerRef.current;
    const lineEl = containerEl && containerEl.children[lineIndex];
    if (containerEl && lineEl) {
      containerEl.focus();
      setCaretOffset(lineEl, offset);
    }
  }, [body]);

  // Bit timings are written as a data-timing attribute on each header
  // <div> and drawn by a CSS ::after (see .wysiwyg-bit-header in
  // index.css) rather than as real text, so they never end up in
  // textContent, the reconstructed raw body, the caret math, or a copied
  // selection. They're set directly on the live DOM instead of baked into
  // buildBodyHtml, so a keystroke inside a bit (which changes its timing
  // but not any header's text) doesn't force an innerHTML rewrite. Runs
  // after every render since any innerHTML rewrite drops the attributes.
  useEffect(() => {
    const containerEl = containerRef.current;
    if (!containerEl) return;
    const labels = getHeaderTimingLabels(body, wordsPerMinute);
    containerEl.querySelectorAll(':scope > [data-header="true"]').forEach((el, i) => {
      const label = labels[i] ?? "";
      if (el.getAttribute("data-timing") !== label) el.setAttribute("data-timing", label);
    });
  });

  // Tracks which single line (if any) the selection is currently inside,
  // for the formatting toolbar (enable/disable, "is this a header"
  // state) - via selectionchange rather than focus/blur, since there's
  // only one focusable element now (the container). A selection spanning
  // multiple lines reports null (Punchline/Bit/Stage act on one line).
  useEffect(() => {
    const handleSelectionChange = () => {
      const containerEl = containerRef.current;
      const sel = window.getSelection();
      let idx = null;
      if (containerEl && sel && sel.rangeCount > 0 && containerEl.contains(sel.anchorNode)) {
        const anchor = getLineIndexAt(containerEl, sel.anchorNode);
        const focus = getLineIndexAt(containerEl, sel.focusNode);
        idx = anchor != null && anchor === focus ? anchor : null;
      }
      setActiveLineState(idx);
      if (onActiveLineChange) onActiveLineChange(idx);
    };
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, [onActiveLineChange]);

  // Returns the line index the selection is currently collapsed/confined
  // to, or null if there's no selection in the editor or it spans
  // multiple lines. Computed fresh from the DOM (rather than trusting the
  // selectionchange-driven state above) so toolbar actions always act on
  // exactly what's selected right now.
  const getActiveLineIndex = () => {
    const selRange = getSelectionRange(containerRef.current, lines);
    if (!selRange) return null;
    return selRange.start.lineIndex === selRange.end.lineIndex ? selRange.start.lineIndex : null;
  };

  const handleInput = () => {
    if (!canEdit()) return;
    const containerEl = containerRef.current;
    const sel = window.getSelection();
    let caretInfo = null;
    if (sel && sel.rangeCount > 0 && containerEl.contains(sel.focusNode)) {
      const lineIndex = getLineIndexAt(containerEl, sel.focusNode);
      if (lineIndex != null) {
        const lineEl = containerEl.children[lineIndex];
        caretInfo = {
          lineIndex,
          offset: getCaretOffset(lineEl, { startContainer: sel.focusNode, startOffset: sel.focusOffset }),
        };
      }
    }
    const newBody = reconstructBody(containerEl);
    if (caretInfo) pendingCaret.current = caretInfo;
    recordHistory(newBody, caretInfo, true); // coalescing: groups a burst of typing into one undo step
    setBody(newBody);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    const isUndo = (e.key === "z" || e.key === "Z") && (e.metaKey || e.ctrlKey) && !e.shiftKey;
    const isRedo =
      ((e.key === "z" || e.key === "Z") && (e.metaKey || e.ctrlKey) && e.shiftKey) ||
      ((e.key === "y" || e.key === "Y") && e.ctrlKey);
    if (isUndo || isRedo) {
      e.preventDefault();
      if (!canEdit()) return;
      if (isUndo) undo();
      else redo();
      return;
    }

    const containerEl = containerRef.current;
    const selRange = getSelectionRange(containerEl, lines);
    if (!selRange) return;

    if (e.key === "Enter") {
      e.preventDefault();
      if (!canEdit()) return;
      const { lines: collapsed, caretLine, caretRaw } = replaceRange(lines, selRange.start, selRange.end, "");
      const rawLine = collapsed[caretLine] ?? "";
      const newLines = collapsed.slice();
      newLines[caretLine] = rawLine.slice(0, caretRaw);
      newLines.splice(caretLine + 1, 0, rawLine.slice(caretRaw));
      commit(newLines, { lineIndex: caretLine + 1, offset: 0 });
      return;
    }

    if (e.key === "Backspace" && selRange.collapsed) {
      const { lineIndex, rawOffset } = selRange.start;
      const rawLine = lines[lineIndex] ?? "";
      const header = parseHeaderLine(rawLine);
      if (header && rawOffsetToDisplayOffset(rawLine, rawOffset) === 0) {
        // Demote the header to a plain line first (common rich-text
        // convention), rather than immediately merging upward.
        e.preventDefault();
        if (!canEdit()) return;
        const newLines = lines.slice();
        newLines[lineIndex] = header.name;
        commit(newLines, { lineIndex, offset: 0 });
      }
      // Otherwise: let the browser handle it natively (deleting within
      // the line, or merging with the previous line/header at a true
      // line start) - handleInput reconstructs the result afterward.
    }
    // All other keys, including a non-collapsed selection's Backspace/
    // Delete/typing-over and arrow-key navigation (which moves correctly
    // across lines on its own now that they share one editable region),
    // are left to native behavior.
  };

  // Prefers the raw text tucked into our own text/html payload (see
  // buildClipboardHtml) when present - so pasting a punchline/header back
  // into this editor keeps its formatting - and falls back to text/plain
  // (the clean display text) for anything copied from somewhere else.
  const getPastedRawText = (clipboardData) => {
    const html = clipboardData.getData("text/html");
    const fromOurHtml = html ? extractRawTextFromClipboardHtml(html) : null;
    return fromOurHtml != null ? fromOurHtml : clipboardData.getData("text/plain");
  };

  const handlePaste = (e) => {
    e.preventDefault();
    if (!canEdit()) return;
    const pasted = getPastedRawText(e.clipboardData);
    if (!pasted) return;
    const selRange = getSelectionRange(containerRef.current, lines);
    if (!selRange) return;
    const { lines: newLines, caretLine, caretRaw } = replaceRange(lines, selRange.start, selRange.end, pasted);
    commit(newLines, {
      lineIndex: caretLine,
      offset: rawOffsetToDisplayOffset(newLines[caretLine] ?? "", caretRaw),
    });
  };

  // Puts the clean display text (markers stripped) on the clipboard as
  // text/plain - the readable words, not the "*"/"()"/"=="/"[]" syntax,
  // for pasting into some other app - and the exact raw text as a
  // text/html payload (see buildClipboardHtml) so pasting back into this
  // editor (or into raw view) keeps the punchline/header formatting
  // instead of losing it to the plain-text round trip.
  const copySelectionToClipboard = (e, selRange) => {
    e.clipboardData.setData("text/plain", extractDisplayText(lines, selRange.start, selRange.end));
    e.clipboardData.setData(
      "text/html",
      buildClipboardHtml(
        extractRawText(lines, selRange.start, selRange.end),
        extractDisplayText(lines, selRange.start, selRange.end)
      )
    );
  };

  const handleCopy = (e) => {
    const selRange = getSelectionRange(containerRef.current, lines);
    if (!selRange || selRange.collapsed) return;
    e.preventDefault();
    copySelectionToClipboard(e, selRange);
  };

  const handleCut = (e) => {
    const selRange = getSelectionRange(containerRef.current, lines);
    if (!selRange || selRange.collapsed) return;
    e.preventDefault();
    copySelectionToClipboard(e, selRange);
    if (!canEdit()) return;
    const { lines: newLines, caretLine, caretRaw } = replaceRange(lines, selRange.start, selRange.end, "");
    commit(newLines, {
      lineIndex: caretLine,
      offset: rawOffsetToDisplayOffset(newLines[caretLine] ?? "", caretRaw),
    });
  };

  // Returns the current single-line selection in raw-text offsets, or
  // null if nothing's selected or the selection spans multiple lines.
  const getActiveSelectionRawRange = () => {
    if (disabled) return null;
    const selRange = getSelectionRange(containerRef.current, lines);
    if (!selRange || selRange.collapsed) return null;
    if (selRange.start.lineIndex !== selRange.end.lineIndex) return null;
    const lineIndex = selRange.start.lineIndex;
    return { lineIndex, rawLine: lines[lineIndex] ?? "", rawLo: selRange.start.rawOffset, rawHi: selRange.end.rawOffset };
  };

  const applyMarkup = (toggleFn) => {
    const selection = getActiveSelectionRawRange();
    if (!selection) return;
    const result = toggleFn(selection.rawLine, selection.rawLo, selection.rawHi);
    if (!result) return;

    const newLines = lines.slice();
    newLines[selection.lineIndex] = result.line;
    commit(newLines, {
      lineIndex: selection.lineIndex,
      offset: rawOffsetToDisplayOffset(result.line, result.caret),
    });
  };

  // Marks the current selection as a punchline. If the selection touches
  // any existing punchline at all - even just part of a multi-word one -
  // every punchline it touches is unmarked instead (selecting the whole
  // line removes every punchline on it); only when it touches none does
  // it wrap the selection as a new one.
  const applyBold = () => applyMarkup(applyPunchlineToggle);

  // Same toggle behavior as applyBold, for stage directions ("[...]").
  const applyStageDirection = () => applyMarkup(applyStageToggle);

  // Turns the focused line into a bit header (the whole line's text
  // becomes the bit's name), or - if it already is one - turns it back
  // into a plain line. No selection needed; this is a whole-line toggle.
  const applyBitHeader = () => {
    if (disabled) return;
    const idx = getActiveLineIndex();
    if (idx == null) return;
    if (!canEdit()) return;
    const rawLine = lines[idx] ?? "";
    const header = parseHeaderLine(rawLine);
    const newLines = lines.slice();

    if (header) {
      newLines[idx] = header.name;
      commit(newLines, { lineIndex: idx, offset: rawOffsetToDisplayOffset(newLines[idx], header.name.length) });
    } else {
      newLines[idx] = `== ${rawLine}`;
      commit(newLines, { lineIndex: idx, offset: rawLine.length });
    }
  };

  const handleContainerMouseDown = (e) => {
    const containerEl = containerRef.current;
    if (e.target !== containerEl) return;
    // Clicked the empty space below the last line - focus its end.
    e.preventDefault();
    const lastIdx = lines.length - 1;
    const lastRaw = lines[lastIdx] ?? "";
    const lineEl = containerEl.children[lastIdx];
    containerEl.focus();
    if (lineEl) setCaretOffset(lineEl, rawOffsetToDisplayOffset(lastRaw, lastRaw.length));
  };

  useImperativeHandle(ref, () => ({ applyBold, applyBitHeader, applyStageDirection }));

  return (
    <div
      ref={containerRef}
      contentEditable={!disabled}
      suppressContentEditableWarning
      spellCheck={false}
      autoCorrect="off"
      autoCapitalize="off"
      onMouseDown={handleContainerMouseDown}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onCopy={handleCopy}
      onCut={handleCut}
      dangerouslySetInnerHTML={{ __html: buildBodyHtml(lines) }}
      className="w-full flex-1 min-h-0 border rounded px-2 py-1 mt-1 bg-gray-50"
      style={{
        overflowY: "auto",
        fontFamily: "'Courier Prime', 'Courier New', monospace",
        fontSize: "inherit",
        minHeight: "120px",
      }}
    />
  );
});

export default WysiwygBody;
